import React, { FC, KeyboardEvent, useCallback, useEffect, useRef } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import AdvancedSearch from '@/assets/icons/AdvancedSearch';
import CloseXs from '@/assets/icons/CloseXs';
import Loader from '@/assets/icons/Loader';
import Search from '@/assets/icons/Search';
import { useBamAuth, useHasPermission } from '@/auth';
import useClientState from '@/state/useClientState';

import { SearchAutocomplete, SearchParams, SearchType } from '../../../types';
import useSearchPanels from '../../hooks/useSearchPanels';
import useSearchState from '../../hooks/useSearchState';
import { getClientNameFromSearchType } from '../../utils/helpers';
import { mapChipToSearchType } from '../../utils/searchAutocomplete';
import { getSearchValue } from '../../utils/searchInput';
import { validateSearchQuery } from '../../utils/searchValidations';
import ChipButton from '../../v1Overwrites/ChipButton/ChipButton';
import SearchButton from '../SearchButton/SearchButton';
import SearchDate from '../SearchDate/SearchDate';
import SearchInputAutocompletePreview from '../SearchInputAutocompletePreview/SearchInputAutocompletePreview';
import SearchInputError from '../SearchInputError/SearchInputError';
import PanelList from '../SearchPanel/PanelList';
import SearchPanel from '../SearchPanel/SearchPanel';
import SearchPanelLi from '../SearchPanel/SearchPanelLi';
import SearchPanelTitle from '../SearchPanel/SearchPanelTitle';
import SearchTypeButtons from '../SearchTypeButtons/SearchTypeButtons';
import SearchTypeLabel from '../SearchTypeLabel/SearchTypeLabel';

import iconStyles from '../../styles/searchIcons.module.css';
import styles from './ExtendedSearch.module.css';

interface ExtendedSearchInputProps extends React.HTMLAttributes<HTMLInputElement> {
  loading?: boolean;
  hideSearchIcon?: boolean;
  defaultValues?: SearchParams;
  isWithinAdvancedSearch?: boolean;
  handleResetFieldWithinAdvancedSearch?: () => void;

  clearSearchField?: boolean;
  /** Callback die wordt afgevuurd als de gebruiker op enter drukt na het invoeren van een searchType / searchInput string. */
  onUserSearchIntent?: (searchParams: SearchParams, add?: boolean) => void;

  /** Callback die wordt gebruikt om het geavanceerde zoek dialoog te openen. */
  handleOpenAdvancedSearchDialog?: () => void;
}

const ExtendedSearch: FC<ExtendedSearchInputProps> = ({
  loading,
  onUserSearchIntent,
  defaultValues,
  isWithinAdvancedSearch = false,
  handleResetFieldWithinAdvancedSearch,
  clearSearchField,
  hideSearchIcon = false,
  handleOpenAdvancedSearchDialog,
}) => {
  // UX of the SearchField
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const {
    searchState,
    setSearchState,
    query,
    searchType,
    rawQuery,
    previewSearchType,
    selectedIndex,
    showPanel,
    setSelectedIndex,
    handleOnChange,
    handleOnFocus,
    handleOnClick: handleOnClickState,
    closeExtendedSearch,
    handleSelectSearchType,
    resetState,
    clearSearchType,
    setHistoryItem,
    isValid,
    hasError,
    suggestions,
    isInputChipActive,
    searchDate,
    setSearchDateV2,
    isFocused,
    setIsFocused,
    showJSONChip,
    handleShowJSONButtonClicks,
    returnAutocompleteSuggestion: returnAutocompleteSuggestionState,
  } = useSearchState(isWithinAdvancedSearch);

  // v2 Global state integration.
  const addSearchResults = useClientState((state) => state.addSearchResults);
  const setAddSearchResults = useClientState((state) => (checked: boolean) => {
    state.toggleAddSearchResults(checked);
    inputRef?.current?.focus();
  });

  /**
   * Focus the input element or just focus the state.
   * @param focusState
   */
  const focusInput = (focusState: boolean = false) => {
    if (focusState) handleOnFocus();
    inputRef.current?.focus();
  };

  /** NEW LIST HOOK */
  const { searchHistory, updateSearchHistory, arrowDown, arrowUp } = useSearchPanels({
    searchType,
    showPanel,
    isWithinAdvancedSearch,
    suggestions,
    selectedIndex,
    setSelectedIndex,
  });

  const getSearchParams = useCallback((): SearchParams | undefined => {
    const { createSearchParams } = validateSearchQuery({
      query: searchState.query,
      searchType: searchState.searchType,
      isWithinAdvancedSearch,
    });
    return createSearchParams({ searchDate: searchState.searchDate });
  }, [searchState, query, searchType, searchDate, isWithinAdvancedSearch]);

  /**
   * Action search will execute the search request.
   * Optionally overwrite SearchParams.
   */
  const actionSearch = useCallback(
    (_searchParams?: SearchParams) => {
      const searchParams = _searchParams ?? getSearchParams();
      if (!searchParams) {
        console.error('Invalid searchParams');
        return;
      }
      if (searchParams.searchType !== 'json') updateSearchHistory(searchParams);
      if (onUserSearchIntent) onUserSearchIntent(searchParams, addSearchResults);
      inputRef.current?.blur();
      closeExtendedSearch();
    },
    [searchState, addSearchResults, getSearchParams],
  );

  /**
   * Preview an autocomplete suggestion.
   * @param suggestion
   * @param index
   */
  const returnAutocompleteSuggestion = (suggestion: SearchAutocomplete, index: number) => {
    /** @todo automatic search after click: it will use the wrong query in the searchParams. */
    const searchParams = returnAutocompleteSuggestionState(suggestion, index);
    if (searchParams?.searchType) actionSearch(searchParams);
  };

  useEffect(() => {
    if (clearSearchField) {
      handleClearAll();
    }
    if (defaultValues) {
      setChipInsideInput(defaultValues.searchType);
      if (defaultValues.searchModality === 'range') {
        setSearchState((prev) => ({
          ...prev,
          query: `${defaultValues.fromValue.toString()}-${defaultValues.toValue.toString()}`,
        }));
      } else {
        setSearchState((prev) => ({ ...prev, query: defaultValues.searchValue.toString() }));
      }
    }
  }, [clearSearchField, defaultValues]);

  /**
   * Click handler for the SearchIcon.
   * The SearchIcon is not shown within the AdvancedSearchDialog.
   */
  const onSearchIconClick = useCallback(() => {
    setIsFocused(true);
    inputRef.current?.focus();
    if (isValid) actionSearch();
  }, [isValid, actionSearch]);

  const handleRemoveChipOnly = useCallback(() => {
    if (isWithinAdvancedSearch) {
      handleResetFieldWithinAdvancedSearch?.();
    }
    clearSearchType();
  }, [handleResetFieldWithinAdvancedSearch, isWithinAdvancedSearch]);

  /**
   *
   */
  const keyboardEventHandler = useCallback(
    (e: KeyboardEvent<HTMLDivElement>) => {
      if (!e.altKey && !e.shiftKey && !e.ctrlKey) e.stopPropagation();
      if (['Enter', 'NumpadEnter', 'ArrowUp', 'Escape', 'ArrowDown'].includes(e.key)) e.preventDefault();

      const { query, searchType } = searchState;

      /** When a user presses enter it depends on the current searchState what happens next:
       * - When history item is selected, it will set the searchState to ui and performs a search.
       * - When date item is selected, it will set the date to searchState and directly performs a search.
       * - When an autocomplete search suggestion is selected, it will preview that searchState and
       *    performs a search, or goes to the date selection panel ico searchType being 'train'|'omloop'.
       * - When user presses enter and everything is valid, it will perform a search.
       */
      const handleEnter = () => {
        if (showPanel === 'searchPanel') {
          const historyItem = searchHistory[selectedIndex];
          setHistoryItem(historyItem);
          actionSearch(historyItem);
        } else if (showPanel === 'searchAutocomplete') {
          const validation = validateSearchQuery({
            searchType: searchState.searchType,
            query: searchState.query,
            isWithinAdvancedSearch,
          });
          actionSearch(validation.createSearchParams({ searchDate }) as SearchParams);
        } else if (query.length && isValid) {
          const { createSearchParams } = validateSearchQuery({
            searchType: searchState.searchType,
            query,
            isWithinAdvancedSearch,
          });
          const searchParams = createSearchParams({ searchDate }) as SearchParams;
          if (searchParams) actionSearch(searchParams);
        }
      };

      // Handle enter
      if (['Enter', 'NumpadEnter'].includes(e.key)) handleEnter();
      if (e.key === 'Escape') closeExtendedSearch();
      if (e.key === 'ArrowUp') arrowUp();
      if (e.key === 'ArrowDown') arrowDown();

      if (e.key === 'Backspace' && !query.length) {
        if (searchType !== 'empty') handleRemoveChipOnly();
        else if (searchDate) setSearchDateV2(null);
      }
    },
    [searchState, searchDate, searchHistory, arrowDown, arrowUp, searchHistory, isValid],
  );

  /**
   * This handles the up and down navigation for searchAutocomplete
   */
  useEffect(() => {
    if (showPanel === 'searchAutocomplete') {
      if (selectedIndex >= 1 && suggestions.length - 1 >= 0 && suggestions?.[selectedIndex - 1])
        returnAutocompleteSuggestion(suggestions[selectedIndex - 1], selectedIndex);
      if (selectedIndex === 0) {
        if (
          (!rawQuery.includes(',') && !rawQuery.includes(';') && searchType === 'empty') ||
          previewSearchType === 'empty'
        )
          clearSearchType();
        setSearchState((prev) => ({ ...prev, query: rawQuery }));
      }
    }
  }, [showPanel, rawQuery, searchType, previewSearchType, selectedIndex]);

  /** Handle for when the user clicks on a searchType */
  const setChipInsideInput = (_searchType: SearchType) => {
    handleSelectSearchType(_searchType, true);
    inputRef?.current?.focus();
  };

  const handleClearAll = useCallback(() => {
    resetState();
    if (inputRef?.current) inputRef.current?.blur();
    if (isWithinAdvancedSearch && handleResetFieldWithinAdvancedSearch) handleResetFieldWithinAdvancedSearch();
  }, [inputRef, isWithinAdvancedSearch, handleResetFieldWithinAdvancedSearch]);

  const handleHistoryItemClick = useCallback(
    (historyItem: SearchParams) => {
      if (!historyItem || (isWithinAdvancedSearch && historyItem.searchType === 'rollingStockType')) return;
      setHistoryItem(historyItem);
      actionSearch(historyItem);
    },
    [isWithinAdvancedSearch],
  );

  useEffect(() => {
    // Prevent autofocus if within isAdvancedSearch
    if (isWithinAdvancedSearch) closeExtendedSearch();
  }, [isWithinAdvancedSearch]);

  useEffect(() => {
    if (isWithinAdvancedSearch) inputRef.current?.blur();
  }, []);

  const { isLoggedIn } = useBamAuth();
  const isAllowedToView = useHasPermission('mwd.view');

  /** Replacement for the old onBlur handler */
  useOnClickOutside(containerRef as React.RefObject<HTMLElement>, () => {
    closeExtendedSearch();
    if (isValid && isWithinAdvancedSearch && ['train', 'rollingStock'].includes(searchType)) actionSearch();
  });

  return (
    <div ref={containerRef} className={styles.searchContainer} onKeyDown={keyboardEventHandler}>
      <div className={[styles.searchBar, isFocused ? styles.focus : ''].join(' ')}>
        {!hideSearchIcon && loading ? (
          <div className={styles.loadingIcon}>
            <Loader className={iconStyles.loaderIcon} />
          </div>
        ) : (
          <div style={{ width: '24px', height: '24px' }}>
            <SearchButton onClick={onSearchIconClick} data-testid='search-icon'>
              <Search className={iconStyles.searchIcon} />
            </SearchButton>
          </div>
        )}
        <div
          style={{
            display: 'flex',
            gap: '8px',
            flexDirection: !searchDate ? 'row-reverse' : 'row',
            alignItems: 'start',
            width: 'var(--search-bar-inner-width)',
          }}
        >
          <SearchDate focusInput={focusInput} searchDate={searchDate} setSearchDate={setSearchDateV2} />
          <div style={{ display: 'flex', gap: '8px', width: '100%', alignItems: 'start' }}>
            {searchType !== 'empty' && isInputChipActive && (
              <div style={{ maxHeight: '24px' }}>
                <ChipButton
                  testid='searchTypeSelected'
                  size='small'
                  transparentHover
                  handleOnClose={handleRemoveChipOnly}
                  label={getClientNameFromSearchType(searchType)}
                />
              </div>
            )}
            <div style={{ flex: '1 1 100%' }}>
              <div style={{ maxHeight: '24px' }}>
                <input
                  ref={inputRef}
                  type='text'
                  id='search-input'
                  data-testid='searchInputTestId'
                  placeholder='Zoek op nummer of reeks'
                  tabIndex={0}
                  minLength={3}
                  autoComplete='off'
                  value={query}
                  title={!isLoggedIn ? 'Log eerst in om te kunnen zoeken' : ''}
                  disabled={!isAllowedToView}
                  onClick={handleOnClickState}
                  onFocus={() => {
                    handleOnFocus();
                  }}
                  onChange={(event) => {
                    handleOnChange(event.target.value);
                  }}
                  className={styles.searchInputV2}
                />
              </div>
            </div>
            {query && (
              <div style={{ maxHeight: '24px' }}>
                <SearchButton onClick={handleClearAll}>
                  <CloseXs className={iconStyles.closeXsIcon} data-testid='clear-input' />
                </SearchButton>
              </div>
            )}
          </div>
        </div>

        <div style={{ maxHeight: '24px' }}>
          <SearchButton
            onClick={() => handleOpenAdvancedSearchDialog && handleOpenAdvancedSearchDialog()}
            data-testid='advancedSearchButton'
          >
            <AdvancedSearch className={iconStyles.openAdvancedIcon} />
          </SearchButton>
        </div>
      </div>
      {showPanel && (
        <SearchPanel
          add={isWithinAdvancedSearch ? undefined : addSearchResults}
          setAdd={isWithinAdvancedSearch ? undefined : setAddSearchResults}
        >
          {showPanel === 'searchPanel' && (
            <div style={{ textAlign: 'left' }}>
              <SearchPanelTitle
                data-testid='search-panel-title'
                title='Waar wil je op zoeken?'
                onClick={handleShowJSONButtonClicks()}
              />
              <SearchTypeButtons
                setChipInsideInput={setChipInsideInput}
                isWithinAdvancedSearch={isWithinAdvancedSearch}
                showJSONChip={showJSONChip}
              />
              <SearchPanelTitle variant='history' title={searchHistory.length > 0 ? 'Recente zoekopdrachten' : ''} />
              <PanelList<SearchParams>
                data={searchHistory}
                row={(history, index) => (
                  <SearchPanelLi
                    index={Number(index)}
                    returnSelectedListItem={() => {
                      handleHistoryItemClick(history);
                    }}
                    selectedListIndex={selectedIndex}
                    key={`history_item_${JSON.stringify(history)}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <SearchTypeLabel searchType={history.searchType} />
                      <div style={{ fontWeight: 500 }}>{getSearchValue(history)}</div>
                      <div style={{ fontWeight: 'normal' }}>{history.description}</div>
                    </div>
                  </SearchPanelLi>
                )}
              />
            </div>
          )}
          {showPanel === 'searchAutocomplete' && (
            <>
              <SearchInputAutocompletePreview
                searchType={previewSearchType}
                isSelected={selectedIndex === 0}
                onClick={() => {
                  setSearchState((prev) => ({
                    ...prev,
                    query: rawQuery,
                    selectedIndex: 0,
                    searchType: prev.previewSearchType,
                  }));
                  inputRef.current?.focus();
                }}
              >
                {rawQuery}
              </SearchInputAutocompletePreview>
              <div style={{ width: '100%', border: ' 1px solid var(--tractie-color-border-default)' }} />
              <PanelList<SearchAutocomplete>
                data={suggestions}
                row={(suggestion, index) => (
                  <SearchPanelLi
                    index={Number(index) + 1} // plus 1 is because the rawQuery also needs to be selectable, basically treat as after a .shift(item).
                    onClick={() => returnAutocompleteSuggestion(suggestion, Number(index) + 1)}
                    selectedListIndex={selectedIndex}
                    key={`suggestion_${suggestion.chip}_${suggestion.waarde ?? suggestion.van}_${suggestion.tot}`}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <SearchTypeLabel searchType={mapChipToSearchType(suggestion.chip)} />
                      <div>{suggestion.waarde ?? `${suggestion.van} - ${suggestion.tot}`}</div>
                      <div style={{ marginLeft: 'auto' }}>({suggestion.aantalWerklijnen})</div>
                    </div>
                  </SearchPanelLi>
                )}
              />
            </>
          )}
        </SearchPanel>
      )}
      {hasError && <SearchInputError smallGap={isWithinAdvancedSearch} errorText='Geen geldige invoer.' />}
    </div>
  );
};
export default ExtendedSearch;
